// Global SFX bus. Volume + mute parallel battleAudio's, separately
// persisted, so the user can balance music vs. SFX independently.
// Each playSfx() allocates a fresh Audio so rapid repeats layer.

const VOL_KEY = "sfxVolume";
const MUTE_KEY = "sfxMuted";

export const SFX_NAMES = [
  "ai_attack_thud",
  "ai_boot_stomp_heavy",
  "ai_boot_stomp_light",
  "ai_boot_stomp_medium",
  "ai_button_click",
  "ai_card_thud",
  "ai_error_sound",
  "ai_happy_harp_strum",
  "ai_harp_strum_sad",
  "ai_level_up",
  "ai_notice_or_warning_sound",
  "ai_pop",
  "ai_opponent_loses_life",
  "ai_orchestral_hit_lute_large",
  "ai_orchestral_hit_lute_medium",
  "ai_orchestral_hit_lute_small",
  "ai_pack_rip_open_sound",
  "ai_paper_flutter",
  "ai_player_loses_life",
  "ai_quill_scratch",
  "ai_spell_cast",
  "ai_sword_being_sheathed",
  "ai_sword_unsheath",
  "ai_trap_violin_hit",
  "ai_turn_start_trumpet",
  "ai_very_quiet_tap",
  "furnace_lit",
  "food_finished_sizzle",
  "munch",
] as const;

export type SfxName = (typeof SFX_NAMES)[number];

let _volume = readVolume();
let _muted = readMuted();
const _listeners = new Set<() => void>();

function readVolume(): number {
  try {
    const raw = window.localStorage.getItem(VOL_KEY);
    if (raw == null) return 100;
    const n = Number(raw);
    if (!Number.isFinite(n)) return 100;
    return clamp(n);
  } catch {
    return 100;
  }
}

function readMuted(): boolean {
  try {
    return window.localStorage.getItem(MUTE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeVolume(v: number) {
  try {
    window.localStorage.setItem(VOL_KEY, String(v));
  } catch {
    // ignore — playback still works without persistence
  }
}

function writeMuted(m: boolean) {
  try {
    window.localStorage.setItem(MUTE_KEY, m ? "1" : "0");
  } catch {
    // ignore — playback still works without persistence
  }
}

function clamp(v: number): number {
  if (v < 0) return 0;
  if (v > 100) return 100;
  return Math.round(v);
}

function notify() {
  for (const cb of _listeners) cb();
}

export function getSfxVolume(): number {
  return _volume;
}

export function setSfxVolume(v: number): void {
  const next = clamp(v);
  if (next === _volume) return;
  _volume = next;
  writeVolume(next);
  notify();
}

export function isSfxMuted(): boolean {
  return _muted;
}

export function setSfxMuted(m: boolean): void {
  if (m === _muted) return;
  _muted = m;
  writeMuted(m);
  notify();
}

/** Effective 0..1 gain — 0 if muted, otherwise volume/100. */
export function effectiveSfxGain(): number {
  return _muted ? 0 : _volume / 100;
}

/** Subscribe to volume/mute changes. Returns an unsubscribe fn. */
export function subscribeSfx(cb: () => void): () => void {
  _listeners.add(cb);
  return () => {
    _listeners.delete(cb);
  };
}

export interface PlaySfxOpts {
  /** Stereo pan: -1 (full left) .. 1 (full right). Default 0. */
  pan?: number;
  /** Pitch / playback rate. 1.0 = normal; 0.85..1.15 = ±15% sounds
   *  natural for a varied pop. Affects duration too — higher rate
   *  also plays faster. */
  pitch?: number;
}

// Cached decoded buffers + a single AudioContext, lazy-initialized
// on first call. We use Web Audio (rather than HTMLAudioElement) so
// pitch and pan can be applied per-shot. Falls back to a plain
// `new Audio` if Web Audio isn't available (jsdom, ancient browsers).
let _ctx: AudioContext | null = null;
const _bufferCache = new Map<string, AudioBuffer>();
const _bufferLoading = new Map<string, Promise<AudioBuffer | null>>();

function getCtx(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (_ctx) return _ctx;
  // Some browsers gate AudioContext construction on a user gesture;
  // we tolerate failure here and fall back to HTMLAudioElement.
  try {
    const Ctor =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!Ctor) return null;
    _ctx = new Ctor();
    return _ctx;
  } catch {
    return null;
  }
}

async function loadBuffer(name: SfxName, ctx: AudioContext): Promise<AudioBuffer | null> {
  const cached = _bufferCache.get(name);
  if (cached) return cached;
  const inflight = _bufferLoading.get(name);
  if (inflight) return inflight;
  const p = (async () => {
    try {
      const r = await fetch(`/audio/${name}.mp3`);
      if (!r.ok) return null;
      const ab = await r.arrayBuffer();
      const buf = await ctx.decodeAudioData(ab);
      _bufferCache.set(name, buf);
      return buf;
    } catch {
      return null;
    } finally {
      _bufferLoading.delete(name);
    }
  })();
  _bufferLoading.set(name, p);
  return p;
}

function playViaHtmlAudio(name: SfxName, gain: number, opts: PlaySfxOpts) {
  try {
    const a = new Audio(`/audio/${name}.mp3`);
    a.volume = gain;
    if (opts.pitch != null) a.playbackRate = opts.pitch;
    void a.play().catch(() => {});
  } catch {
    // jsdom / autoplay denial / etc.
  }
}

/** Fire-and-forget. No-ops outside the browser and on autoplay denial. */
export function playSfx(name: SfxName, opts: PlaySfxOpts = {}): void {
  if (typeof window === "undefined") return;
  const gain = effectiveSfxGain();
  if (gain <= 0) return;

  const ctx = getCtx();
  if (!ctx) {
    playViaHtmlAudio(name, gain, opts);
    return;
  }

  // If the audio context was suspended by autoplay policy (very
  // common on first interaction), resume it. Safe to call repeatedly.
  if (ctx.state === "suspended") void ctx.resume().catch(() => {});

  void loadBuffer(name, ctx).then((buf) => {
    if (!buf) {
      playViaHtmlAudio(name, gain, opts);
      return;
    }
    try {
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.playbackRate.value = clampPitch(opts.pitch);

      const gainNode = ctx.createGain();
      gainNode.gain.value = gain;

      let last: AudioNode = src;
      // StereoPannerNode isn't on every browser (notably older
      // Safari). Skip panning if it's missing rather than throwing.
      if (typeof ctx.createStereoPanner === "function") {
        const panner = ctx.createStereoPanner();
        panner.pan.value = clampPan(opts.pan);
        last.connect(panner);
        last = panner;
      }
      last.connect(gainNode);
      gainNode.connect(ctx.destination);

      src.start();
    } catch {
      playViaHtmlAudio(name, gain, opts);
    }
  });
}

function clampPan(v: number | undefined): number {
  if (v == null) return 0;
  if (v < -1) return -1;
  if (v > 1) return 1;
  return v;
}

function clampPitch(v: number | undefined): number {
  if (v == null) return 1;
  if (v < 0.25) return 0.25;
  if (v > 4) return 4;
  return v;
}

/** Test-only. */
export function __resetSfxForTests(): void {
  _volume = 100;
  _muted = false;
  _listeners.clear();
  try {
    window.localStorage.removeItem(VOL_KEY);
    window.localStorage.removeItem(MUTE_KEY);
  } catch {
    // ignore
  }
}
